import { resize } from '@/helpers/canvas'

import soil from './soil'
import bone from './bone'
import roots from './roots'
import rain from './rain'
import debug from './debug'
import { Soil } from './constants'

/**
 * @typedef {Object} CropData
 * @param {Array<Bone>} bones
 * @param {Array<Image>} images
 */

/**
 * @typedef {Object} CropSimulationRenderer
 * @method requestFrame Requests an animation frame
 * @method cancelFrame Cancels the last animation frame
 * @method destroy Destroys the simulation
 */

/**
 * Creates a simulation renderer
 * @param {HTMLCanvasElement} canvas
 * @param {CropData} crop
 * @returns {CropSimulationRenderer}
 */
function create (canvas, crop) {
  let isRunning = false
  let isPaused = true
  let isSyncing = true
  // cropData
  let cropData = crop
  let cursorPosition
  let cursorRadius
  // Frame identifier (used to cancel animation frame).
  let frameID
  let frameData

  // Canvas Context 2D
  const cx = canvas.getContext('2d')

  // Sets the current cursor position
  function setCursorPosition (newCursorPosition, newCursorRadius) {
    cursorPosition = newCursorPosition
    cursorRadius = newCursorRadius
  }

  // Sets the cropData, this is very useful
  // to update crop data from the outside
  // of the simulation once it was initialized.
  function setCropData (newCropData) {
    cropData = newCropData
    roots.destroy()
    roots.init(cx, cropData)
  }

  // Sets simulation data
  function setFrameData (data) {
    frameData = data
  }

  // Initializes everything.
  function init () {
    // Initialize soil layers.
    soil.init(cx)
    // Initialize rain.
    rain.init(cx)
    // Initialize root rendering.
    roots.init(cx, cropData)
    // Initialize debug context.
    debug.init()
  }

  // Updates any required data.
  function update (data, time) {
    bone.updateAll(data, cropData)
    // If the simulation is paused and we aren't syncing
    // then we return without updating.
    if (isPaused && !isSyncing) {
      return false
    }
    soil.update(cx, data)
    rain.update(cx, data, isSyncing ? 1.0 : 0.1)
    if (isSyncing) {
      isSyncing = false
    }
    return true
  }

  // This is in charge of rendering the crop and uses
  // the data generated by the simulation.
  function render (data, time) {
    cx.clearRect(0, 0, cx.canvas.width, cx.canvas.height)
    cx.save()
    cx.translate(cx.canvas.width >> 1, cx.canvas.height * (1 - Soil.HEIGHT))
    // Renders soil and roots
    // Renders rain
    rain.render(cx, data)
    soil.render(cx, data)
    roots.render(cx, data)
    bone.renderAll(cx, data, cropData)
    if (cursorPosition && cursorRadius) {
      const [x, y] = cursorPosition
      cx.beginPath()
      cx.arc(x, y, cursorRadius, 0, Math.PI * 2)
      cx.strokeStyle = '#0ff'
      cx.stroke()
    }
    cx.restore()
  }

  // Frame callback
  function frame (time) {
    const isResized = resize(canvas)
    if (isResized) {
      rain.resize(canvas)
      soil.resize(canvas)
    }
    update(frameData, time)
    render(frameData, time)
    frameID = window.requestAnimationFrame(frame)
  }

  // Starts the simulation
  function start (data) {
    if (isRunning) {
      return false
    }
    isRunning = true
    // sets the frameData
    setFrameData(data)
    frameID = window.requestAnimationFrame(frame)
    return true
  }

  // Stops the simulation
  function stop () {
    if (!isRunning) {
      return false
    }
    isRunning = false
    window.cancelAnimationFrame(frameID)
    frameID = undefined
    frameData = undefined
    destroy()
    return true
  }

  function seek (data, shouldSync = true) {
    if (!isSyncing && shouldSync) {
      isSyncing = true
    }
    setFrameData(data)
  }

  // Pauses the simulation
  function pause () {
    if (!isPaused) {
      isPaused = true
    }
  }

  // Resumes the simulation
  function resume () {
    if (isPaused) {
      isPaused = false
    }
  }

  // Destroys the simulation
  function destroy () {
    if (isRunning) {
      stop()
    }
    roots.destroy()
    soil.destroy()
    rain.destroy()
    debug.destroy()
  }

  // We initialize the simulation.
  init()

  return {
    start,
    stop,
    seek,
    pause,
    resume,
    setCursorPosition,
    setCropData,
    setFrameData,
    destroy
  }
}

/**
 * Initializes simulation rendering
 * @param {HTMLCanvasElement} canvas Simulation canvas
 * @param {CropData} crop Simulation crop data
 */
export async function init (canvas, crop) {
  if (!canvas) {
    throw new Error('Invalid canvas')
  }
  if (!crop) {
    throw new Error('Invalid crop')
  }
  return create(canvas, crop)
}

export default {
  init
}
